import { useLayoutEffect, useMemo, useState, type RefObject } from 'react';

/** Связь между двумя матчами: из fromId выходит линия в toId. */
export interface ConnectorLink {
  fromId: string;
  toId: string;
}

interface Props {
  /** Ref на скроллируемый контейнер сетки (position: relative). */
  containerRef: RefObject<HTMLElement | null>;
  links: ConnectorLink[];
}

interface Segment {
  key: string;
  d: string;
}

/**
 * SVG-оверлей с угловыми линиями-коннекторами между матчами турнирной сетки.
 *
 * Измеряет DOM-позиции матчей (по `data-match-id`) относительно скроллируемого
 * контента контейнера и рисует классические «ступеньки» (горизонталь →
 * вертикаль → горизонталь). Пересчёт — через ResizeObserver.
 */
export function BracketConnectors({ containerRef, links }: Props) {
  const [segments, setSegments] = useState<Segment[]>([]);
  const [size, setSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  // Стабильная строка из связей — зависимость эффекта не зависит от ссылки массива.
  const linksKey = useMemo(
    () => links.map((l) => `${l.fromId}>${l.toId}`).join('|'),
    [links]
  );

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const compute = () => {
      const containerRect = container.getBoundingClientRect();
      // Координаты внутри скроллируемого контента контейнера. SVG позиционируется
      // по padding-box (absolute), поэтому вычитаем толщину border (clientLeft/Top).
      const offsetX = -containerRect.left + container.scrollLeft - container.clientLeft;
      const offsetY = -containerRect.top + container.scrollTop - container.clientTop;

      const find = (id: string) =>
        container.querySelector<HTMLElement>(`[data-match-id="${CSS.escape(id)}"]`);

      const next: Segment[] = [];
      const observed: HTMLElement[] = [];
      for (const link of links) {
        const fromEl = find(link.fromId);
        const toEl = find(link.toId);
        if (!fromEl || !toEl) continue;
        observed.push(fromEl, toEl);

        const fr = fromEl.getBoundingClientRect();
        const tr = toEl.getBoundingClientRect();

        const x1 = fr.right + offsetX;
        const y1 = fr.top + fr.height / 2 + offsetY;
        const x2 = tr.left + offsetX;
        const y2 = tr.top + tr.height / 2 + offsetY;
        const midX = x1 + (x2 - x1) / 2;

        next.push({
          key: `${link.fromId}>${link.toId}`,
          d: `M ${x1} ${y1} H ${midX} V ${y2} H ${x2}`,
        });
      }

      setSegments(next);
      setSize({ w: container.scrollWidth, h: container.scrollHeight });
      return observed;
    };

    const observed = compute();

    // Наблюдаем за контейнером и за самими карточками матчей — изменение
    // размера любой карточки требует пересчёта линий.
    const ro = new ResizeObserver(() => compute());
    ro.observe(container);
    for (const el of new Set(observed)) ro.observe(el);

    window.addEventListener('resize', compute);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', compute);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef, linksKey]);

  if (segments.length === 0) return null;

  return (
    <svg
      aria-hidden
      className="absolute top-0 left-0 pointer-events-none"
      width={size.w}
      height={size.h}
    >
      {segments.map((s) => (
        <path
          key={s.key}
          d={s.d}
          fill="none"
          stroke="var(--line-strong)"
          strokeWidth={1.5}
        />
      ))}
    </svg>
  );
}
